import React, { useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import Colors from '../../../../ui/global/Colors';
import Button from '../../../../ui/components/Button';
import { useSandpack } from '@codesandbox/sandpack-react';
import { GithubFormat } from '../../utils/formatCode';
import { localUntransformCode } from '../../utils/formatCode';
import { useGame } from '../../../../backend/useGame';

// Save button container styling
const SaveButtonContainer = styled.div`
    position: absolute;
    bottom: 10px;
    right: 20px;
    z-index: 1000;
    
    // Only show in Code mode
    display: none;
    [data-mode='Code'] & {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 5px;
    }
`;

// Add this to make TypeScript happy with our global reference
declare global {
    interface Window {
        handleSaveRef?: {
            current: (() => void) | null;
        };
    }
}

interface SaveHandlerProps {
    gameId?: string;
    setHasCodeChanges: (hasChanges: boolean) => void;
    onSaveCodeEdit: (code: GithubFormat) => void;
}

// Component that handles the save functionality
export const SaveHandler: React.FC<SaveHandlerProps> = ({ gameId, setHasCodeChanges, onSaveCodeEdit }) => {
    const { sandpack } = useSandpack();
    const { saveCodeToHistory } = useGame();
    
    const saveCode = useCallback(() => {
        // Get the current files from sandpack
        const { files } = sandpack;
        
        // Convert from Sandpack format to GitHub format
        const githubFormatCode = Object.entries(files)
            .filter(([path, file]) => !path.startsWith('/tsconfig.json') && !path.startsWith('/package.json'))
            .map(([path, file]) => localUntransformCode({ path, content: file.code }, Object.keys(files).length > 1))
            .filter((item): item is Exclude<typeof item, null> => item !== null) // Filter out nulls
            .filter(item => !item.path.includes('tsconfig.json') && !item.path.includes('package.json') && !item.path.includes('package-lock.json'));

        // console.log('Saving code changes in GitHub format:', githubFormatCode);
        onSaveCodeEdit(githubFormatCode as GithubFormat);
        setHasCodeChanges(false); // Reset changes flag after saving
    }, [sandpack, gameId, saveCodeToHistory, setHasCodeChanges]);
    
    // Expose the save function to the parent component
    useEffect(() => {
        if (window.handleSaveRef) {
            window.handleSaveRef.current = saveCode;
        }
        
        return () => {
            // Clean up the reference when component unmounts
            if (window.handleSaveRef) {
                window.handleSaveRef.current = null;
            }
        };
    }, [saveCode]);
    
    return null; // This component doesn't render anything
};

interface SaveButtonProps {
    hasCodeChanges: boolean;
    onClick: () => void;
}

// Component for the save button
export const SaveButton: React.FC<SaveButtonProps> = ({ hasCodeChanges, onClick }) => {
    return (
        <SaveButtonContainer>
            <Button 
                text="SAVE"
                color="white"
                size="tiny"
                onClick={onClick}
                disabled={!hasCodeChanges}
            />
        </SaveButtonContainer>
    );
};

// Setup function to initialize the global reference
export const setupSaveHandler = () => {
    // Create a global reference to store the save function
    window.handleSaveRef = { current: null };
    
    return () => {
        // Clean up the reference when component unmounts
        delete window.handleSaveRef;
    };
};

// Function to trigger the save operation
export const triggerSave = () => {
    // console.log('Saving code changes...');
    // Use the reference to the save function
    if (window.handleSaveRef && window.handleSaveRef.current) {
        window.handleSaveRef.current();
    }
};